import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import ProductGrid from '@components/organisms/ProductGrid'
import Wishlist, { WishListProps } from '@components/organisms/WishlistProducts'
import { store } from '@redux/store'
import { CartProps } from '@interfaces/cart'
import Cart from '../../components/cart/Cart'
import ProductGallery, { ProductGalleryProps } from '../../components/product/ProductGallery'
import ProductForm from '../../components/product/ProductForm'
import { ProductData, TwoColumnSectionProps } from '@interfaces/product'
import AnnouncementBar, { AnnouncementData } from '../../components/announcement-bar/AnnouncementBar'
import ProductList, { ProductListProps } from '../../components/product-list/ProductList'
import FAQ, { FAQData } from '../../components/faq/FAQ'
import SizeGuideSection, { SizeGuideSectionProps } from '../../components/size-guide/SizeGuide'
import TwoColumnSection from '../../components/two-column/TwoColumnSection'
import UnrelatedProducts, { UnrelatedProductsProps } from '../../components/unrelated-products/UnrelatedProducts'
import Search from '../../components/header/Search'
import { ProductGridProps } from '@interfaces/productGrid'
import { debounce } from '@helpers/global'
import RelatedProducts, { RelatedProductsProps } from '../../components/related-products/RelatedProducts'
import Carousel from '../../components/featured-collection/Carousel'
import { CollectionsData } from '@interfaces/collection'
import WishListButton, { WishListButtonProps } from '../components/atoms/WishListButton'
import SizeGuideDrawer, { SizeGuideDrawerProps } from '../../components/size-guide-drawer/SizeGuideDrawer'
import UgcModule, { UgcData } from '../../components/ugc-module/UgcModule'
import TurboFormModal, { TurboFormModalData } from '../../components/12-days-turbo/TurboFormModal'
import TurboPrize, { TurboPrizeData } from '../../components/12-days-turbo/TurboPrize'
import TurboCountdownTimer, { TurboCountdownTimerData } from '../../components/12-days-turbo/TurboCountdownTimer'
import PromotionalBar, { PromotionalData } from '../../components/promotional-banner/PromotionalBanner'
import SportsBeerProductCard, { SportsBeerProductCardData } from '../../components/sports-beer/SportsBeerProductCard'
import SportsBeerAccordion, { SportsBeerAccordionData } from '../../components/sports-beer/SportsBeerAccordion'
import SportsBeerLoadMore from '../../components/sports-beer/SportsBeerLoadMore'
import SportsBeerImageCarousel, { SportsBeerImageCarouselData } from '../../components/sports-beer/SportsBeerImageCarousel'
import SportsBeerCertifiedLegends, { SportsBeerCertifiedLegendsData } from '../../components/sports-beer/SportsBeerCertifiedLegends'
import SportsBeerTV, { SportsBeerTVData } from '../../components/sports-beer/SportsBeerTV'

// Reusable function for rendering components
const renderComponent = <T extends object>(
  rootElementId: string,
  _dataInterface: T,
  Component: React.ComponentType<T & { children?: React.ReactNode }>,
): void => {
  const rootElements = document.querySelectorAll(rootElementId)
  if (!rootElements) return

  rootElements.forEach((rootElement) => {
    const dataEl = rootElement.querySelector('[data-section-data]') as HTMLElement | null
    const dataChildrenEl = rootElement.querySelector('[data-children-data]') as HTMLElement | null
    let sectionData: T = {} as T
    let dataChildren: React.ReactNode = null

    try {
      sectionData = dataEl?.textContent ? JSON.parse(dataEl.textContent) : ({} as T)
    } catch (error) {
      console.error('Error parsing section data:', error)
    }

    if (dataChildrenEl) {
      const rawChildren = dataChildrenEl.innerHTML?.trim()
      try {
        dataChildren = dataChildrenEl.innerHTML ? <div dangerouslySetInnerHTML={{ __html: rawChildren }} /> : null
      } catch (error) {
        console.error('Error parsing children data:', error)
      }
    }

    createRoot(rootElement).render(
      <Provider store={store}>
        <Component {...sectionData}>{dataChildren}</Component>
      </Provider>,
    )
  })
}

// Handle search input changes
const handleSearchChange = debounce((event: Event): void => {
  const searchInput = event.target as HTMLInputElement
  const form = searchInput.closest('[data-main-search]')
  if (!form) return

  const searchResultsContainer = form.querySelector('[data-search-results-container]')
  const searchResultsRoot = form.querySelector('[data-search-results-root]')
  const query = searchInput.value.trim()

  if (!query) {
    searchResultsContainer?.classList.add('hidden')
    return
  }

  fetch(
    `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&section_id=predictive-search-json`,
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }
      return response.text()
    })
    .then((html) => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const jsonContainer = doc.querySelector('#shopify-section-predictive-search-json')

      if (!jsonContainer) {
        throw new Error('JSON container not found in the HTML response')
      }

      const rawJson = jsonContainer.textContent?.trim()
      if (!rawJson) {
        throw new Error('JSON data is empty or missing')
      }

      const jsonData = JSON.parse(rawJson)
      const { products = [] } = jsonData

      if (searchResultsRoot) {
        createRoot(searchResultsRoot).render(
          <Provider store={store}>
            <Search products={products} query={query} />
          </Provider>,
        )
      }

      if (products.length > 0) {
        searchResultsContainer?.classList.remove('hidden')
      } else {
        searchResultsContainer?.classList.add('hidden')
      }
    })
    .catch((error) => {
      console.error('Error fetching search data:', error)
      searchResultsContainer?.classList.add('hidden')
    })
}, 300)

// Initialize search for all instances
const initSearch = (): void => {
  const searchForms = document.querySelectorAll('[data-main-search]')
  if (!searchForms.length) return

  searchForms.forEach((form) => {
    const searchInput = form.querySelector('[data-main-search-input]') as HTMLInputElement | null
    if (!searchInput) return

    searchInput.addEventListener('input', handleSearchChange)

    form.addEventListener('submit', (event) => {
      if (!searchInput.value.trim()) {
        event.preventDefault() // Prevent empty form submission
      }
    })
  })
}

// Render all components
const renderComponents = (): void => {
  initSearch()
  renderComponent('[data-product-grid-root]', {} as ProductGridProps, ProductGrid)
  renderComponent('[data-cart-root]', {} as CartProps, Cart)
  renderComponent('[data-product-gallery-root]', {} as ProductGalleryProps, ProductGallery)
  renderComponent('[data-product-form-root]', {} as ProductData, ProductForm)
  renderComponent('[data-product-list-root]', {} as ProductListProps, ProductList)
  renderComponent('[data-wish-list-root]', {} as WishListProps, Wishlist)
  renderComponent('[data-wish-list-button]', {} as WishListButtonProps, WishListButton)
  renderComponent('[data-announcement-bar-root]', {} as AnnouncementData, AnnouncementBar)
  renderComponent('[data-carousel-root]', {} as CollectionsData, Carousel)
  renderComponent('[data-faq-root]', {} as FAQData, FAQ)
  renderComponent('[data-size-guide-section-root]', {} as SizeGuideSectionProps, SizeGuideSection)
  renderComponent('[data-two-column-section-root]', {} as TwoColumnSectionProps, TwoColumnSection)
  renderComponent('[data-unrelated-products-section-root]', {} as UnrelatedProductsProps, UnrelatedProducts)
  renderComponent('[data-related-products-root]', {} as RelatedProductsProps, RelatedProducts)
  renderComponent('[data-size-guide-drawer-root]', {} as SizeGuideDrawerProps, SizeGuideDrawer)
  renderComponent('[data-ugc-module-root]', {} as UgcData, UgcModule)
  renderComponent('[data-turbo-form-modal-root]', {} as TurboFormModalData, TurboFormModal)
  renderComponent('[data-turbo-prizes-root]', {} as TurboPrizeData, TurboPrize)
  renderComponent('[data-turbo-countdown-root]', {} as TurboCountdownTimerData, TurboCountdownTimer)
  renderComponent('[data-promotional-banner-root]', {} as PromotionalData, PromotionalBar)
  renderComponent('[data-sports-beer-product-card-root]', {} as SportsBeerProductCardData, SportsBeerProductCard)
  renderComponent('[data-sports-beer-accordion-root]', {} as SportsBeerAccordionData, SportsBeerAccordion)
  renderComponent('[data-sports-beer-load-more-root]', {} as object, SportsBeerLoadMore)
  renderComponent('[data-sports-beer-image-carousel-root]', {} as SportsBeerImageCarouselData, SportsBeerImageCarousel)
  renderComponent('[data-sports-beer-certified-legends-root]', {} as SportsBeerCertifiedLegendsData, SportsBeerCertifiedLegends)
  renderComponent('[data-sports-beer-tv-root]', {} as SportsBeerTVData, SportsBeerTV)
}

export default renderComponents
